import React, { useState, useCallback } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import HondaLoader from '../../components/common/HondaLoader';
import { sanitizeInput } from '../../utils/inputValidation';
import { getCessionInformation } from '../../services/cesionesService';
import Swal from 'sweetalert2';

const getEstadoColor = (estado) => {
  const e = (estado || '').toUpperCase();
  if (e.includes('APROBADO')) return 'text-green-600 bg-green-50 border-green-200';
  if (e.includes('RECHAZADO')) return 'text-red-600 bg-red-50 border-red-200';
  if (e.includes('REVISION') || e.includes('PENDIENTE')) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  return 'text-gray-600 bg-gray-50 border-gray-200';
};

const BusquedaCesiones = ({ user, onLogout, onNavigate }) => {
  const [grupo, setGrupo] = useState('');
  const [orden, setOrden] = useState('');
  const [resultado, setResultado] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [hasBuscado, setHasBuscado] = useState(false);

  const handleBuscar = useCallback(async () => {
    if (!grupo || !orden) {
      Swal.fire({ icon: 'warning', title: 'Campos incompletos', text: 'Ingrese grupo y orden para buscar.', confirmButtonColor: '#ef4444' });
      return;
    }
    setBuscando(true);
    setHasBuscado(true);
    setResultado(null);
    try {
      const data = await getCessionInformation({ group: sanitizeInput(grupo), order: sanitizeInput(orden), sellerID: sanitizeInput(user.ce_ID ? user.ce_ID : 0) });
      if (!data || !data.objContract) {
        setResultado(null);
        Swal.fire({ icon: 'info', title: 'Sin resultados', text: 'No se encontro ninguna cesion con esos datos.', confirmButtonColor: '#ef4444' });
        return;
      }
      setResultado({
        cessionID: data.cessionID,
        grupo: data.objContract?.group,
        orden: data.objContract?.order,
        tipoFormulario: data.objContract?.formType,
        personType: data.personType || '',
        estado: data.cessionStatus || 'N/A',
        comprador: { nombre: data.objBuyer?.fullName, dni: data.objBuyer?.ident, tipo: data.objBuyer?.personType },
        vendedor: { nombre: data.objSeller?.fullName, dni: data.objSeller?.ident },
        documentos: data.documents || [],
      });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error en la busqueda', text: err.message || 'No se pudo completar la busqueda.', confirmButtonColor: '#ef4444' });
    } finally {
      setBuscando(false);
    }
  }, [grupo, orden]);

  const handleKeyDown = useCallback(e => { if (e.key === 'Enter') handleBuscar(); }, [handleBuscar]);

  const handleVerDetalle = useCallback((row) => {
    onNavigate('visualizacionCesion', {
      cessionID: row.cessionID,
      grupo: row.grupo !== 'N/A' ? row.grupo : '',
      orden: row.orden !== 'N/A' ? row.orden : '',
      estado: row.estado,
      soloVisualizacion: true,
    });
  }, [onNavigate]);

  return (
    <MainLayout user={user} onLogout={onLogout} onNavigate={onNavigate}>
      <div className="max-w-full mx-auto px-3 py-4">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1.5 h-8 gradient-red rounded-full shadow-glow-red"></div>
            <h2 className="text-2xl font-bold text-gradient">Busqueda de Cesiones</h2>
          </div>
          <p className="text-gray-500 text-sm ml-5">Busque cesiones por grupo, orden o Cession ID</p>
        </div>

        <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-red-100 to-transparent rounded-full opacity-20 blur-3xl"></div>
          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="group">
                <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Grupo</label>
                <div className="relative">
                  <input type="text" placeholder="Ej: 010982" value={grupo}
                    onChange={e => setGrupo(sanitizeInput(e.target.value))} onKeyDown={handleKeyDown}
                    className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all group-hover:border-gray-300"
                  />
                </div>
              </div>
              <div className="group">
                <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Orden</label>
                <div className="relative">
                  <input type="text" placeholder="Ej: 0106" value={orden}
                    onChange={e => setOrden(sanitizeInput(e.target.value))} onKeyDown={handleKeyDown}
                    className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-800 text-sm focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 transition-all group-hover:border-gray-300"
                  />
                </div>
              </div>
              <div>
                <button onClick={handleBuscar} disabled={buscando}
                  className="w-full px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl transition-all text-sm font-semibold shadow-lg shadow-red-200 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
                >
                  <span className="material-icons text-sm">{buscando ? 'hourglass_empty' : 'search'}</span>
                  {buscando ? 'Buscando...' : 'Buscar Cesion'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {buscando ? (
          <HondaLoader message="Buscando cesion..." variant="inline" size="medium" />
        ) : !hasBuscado ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center">
            <span className="material-icons text-5xl text-gray-300 mb-3 block">search</span>
            <p className="text-gray-400">No hay datos disponibles</p>
            <p className="text-gray-300 text-sm mt-1">Ingrese los datos y presione "Buscar Cesion"</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden animate-fade-in">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-red-600 to-red-700">
                  <tr>
                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Cession ID</th>
                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Grupo</th>
                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Orden</th>
                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Tipo Formulario</th>
                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Comprador</th>
                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Vendedor</th>
                    <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">Estado</th>
                    <th className="px-4 py-4 text-center text-xs font-bold text-white uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {resultado ? (
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm font-mono text-gray-900">{resultado.cessionID || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{resultado.grupo || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{resultado.orden || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{resultado.tipoFormulario || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <p className="font-medium">{resultado.comprador?.nombre || '—'}</p>
                        <p className="text-xs text-gray-500">{resultado.comprador?.dni || ''}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <p className="font-medium">{resultado.vendedor?.nombre || '—'}</p>
                        <p className="text-xs text-gray-500">{resultado.vendedor?.dni || ''}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{resultado.estado}</td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleVerDetalle(resultado); }}
                          className="text-blue-600 hover:bg-blue-600 hover:text-white transition-colors p-1 rounded-full"
                          title="Ver detalle"
                        >
                          <span className="material-icons text-xl">visibility</span>
                        </button>
                      </td>
                    </tr>
                  ) : (
                    <tr>
                      <td colSpan="8" className="px-4 py-12 text-center">
                        <span className="material-icons text-5xl text-gray-300 mb-3 block">search_off</span>
                        <p className="text-gray-400">No se encontro ninguna cesion con esos datos</p>
                        <p className="text-gray-300 text-sm mt-1">Verifique los datos ingresados e intente nuevamente</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default BusquedaCesiones;